import { useState } from 'react';
import type { RadioStation } from '@/mocks/radios';
import { getStationEvents, type RadioEvent, eventStatusColors, eventTypeLabels, eventTypeIcons } from '@/mocks/adminEvents';

interface RadioEventosSectionProps {
  station: RadioStation;
}

export default function RadioEventosSection({ station }: RadioEventosSectionProps) {
  const events = getStationEvents(station.id);
  const [activeTab, setActiveTab] = useState<'proximos' | 'finalizados'>('proximos');

  const proximos = events.filter((e) => e.status === 'publicado' || e.status === 'borrador');
  const finalizados = events.filter((e) => e.status === 'finalizado' || e.status === 'cancelado');

  if (events.length === 0) return null;

  const displayEvents = activeTab === 'proximos' ? proximos : finalizados;

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Eventos</h3>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5">
          <button
            onClick={() => setActiveTab('proximos')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
              activeTab === 'proximos' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Próximos ({proximos.length})
          </button>
          <button
            onClick={() => setActiveTab('finalizados')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
              activeTab === 'finalizados' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Finalizados ({finalizados.length})
          </button>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayEvents.slice(0, 6).map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
        {displayEvents.length === 0 && (
          <div className="col-span-full py-8 text-center text-sm text-foreground-500">
            No hay eventos {activeTab === 'proximos' ? 'próximos' : 'finalizados'}
          </div>
        )}
      </div>
    </section>
  );
}

function EventCard({ event }: { event: RadioEvent }) {
  const [interested, setInterested] = useState(false);
  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };
  const status = eventStatusColors[event.status] || { dot: 'bg-foreground-400', text: 'text-foreground-500', bg: 'bg-foreground-100' };
  const typeLabel = eventTypeLabels[event.type] || event.type;
  const typeIcon = eventTypeIcons[event.type] || 'ri-calendar-line';

  return (
    <div className="rounded-xl border border-background-200/70 overflow-hidden bg-background-50 hover:border-background-300/60 transition-all">
      <div className="relative h-32 bg-background-200/70 overflow-hidden">
        <img
          src={event.image}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${status.bg} ${status.text}`}>
            {event.status === 'publicado' ? 'Próximo' : event.status === 'borrador' ? 'Borrador' : event.status === 'finalizado' ? 'Finalizado' : 'Cancelado'}
          </span>
          {event.highlighted && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold whitespace-nowrap">
              Destacado
            </span>
          )}
        </div>
        <div className="absolute bottom-2 left-2 flex items-center gap-1 text-white text-[11px]">
          <i className={typeIcon}></i>
          {typeLabel}
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-1">{event.title}</h4>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500 flex-wrap">
          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {event.date}</span>
          <span className="flex items-center gap-1"><i className="ri-time-line"></i> {event.time}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-foreground-500 mt-1">
          <i className="ri-map-pin-line"></i>
          <span className="truncate">{event.location}</span>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-user-line"></i> {formatNumber(event.participants)}</span>
            <span className="flex items-center gap-1"><i className="ri-star-line"></i> {formatNumber(event.interested)}</span>
          </div>
          {event.status === 'publicado' && (
            <button
              onClick={() => setInterested(!interested)}
              className={`text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap px-3 py-1 rounded-full ${
                interested
                  ? 'bg-accent-100 text-accent-700 border border-accent-200'
                  : 'bg-accent-500 text-background-50 dark:text-foreground-950 hover:bg-accent-600'
              }`}
            >
              {interested ? 'Interesado' : 'Me interesa'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}